/**
 * Invitation Service
 *
 * Service for sending user invitations to join the personal
 * trainer app. Extends the base CrudService with proper
 * error handling.
 */

import { ZodError } from 'zod';

import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import { validateEmail, type EmailInput } from '../validation/common.schemas';

import type { InvitationPayload } from '../types/domain/invitation.types';

// =============================================================================
// INVITATION SERVICE CLASS
// =============================================================================

/**
 * InvitationService - Handles email invitations to join the personal trainer app.
 */
export class InvitationService extends CrudService<EmailInput> {
  constructor() {
    super({
      baseUrl: '/invite',
      enableCaching: false,
    });
  }

  /**
   * Send Invitation email to user
   *
   * POST /api/user/invite
   * Uses common.schemas.ts for client-side validation.
   */
  public async invite(email: EmailInput): Promise<void> {
    try {
      this.validateInvitationData(email);

      const payload: InvitationPayload = { email };
      const response = await apiClient.post('/invite', payload);

      if (response.status !== 201) {
        throw new Error(`Failed to send invitation to ${email}`);
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
      rethrowTyped(error, 'Unable to send invitation. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // VALIDATION AND TRANSFORMATION
  // ---------------------------------------------------------------------------

  /**
   * Validate invitation payload using Zod schema from common.schemas.ts.
   */
  protected validateInvitationData(data: EmailInput): void {
    try {
      validateEmail(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't send the invitation", error, {
          originalData: data,
          details: { service: 'InvitationService' },
        });
      }

      throw new InvalidDataError(
        "Couldn't send the invitation: the data is in an unexpected format",
        {
          originalData: data,
          details: { service: 'InvitationService' },
        }
      );
    }
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default invitation service instance
 * Pre-configured and ready to use throughout the application
 */
export const invitationService = new InvitationService();
